import { G_ANG, G_R_OVERRIDE, G_R, G_DISC, P_R, P_DISC, ORBIT1_R, ORBIT2_R } from './constants';
import type { Group, Program, Person } from './types';

function rad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function pt(r: number, deg: number, ox: number, oy: number): { x: number; y: number } {
  return { x: ox + r * Math.cos(rad(deg)), y: oy + r * Math.sin(rad(deg)) };
}

export function norm(a: number): number {
  return ((a + 180) % 360 + 360) % 360 - 180;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export function personSlots(count: number, awayAngle: number): number[] {
  if (count === 1) return [awayAngle];
  const spread = count === 2 ? 90 : count <= 4 ? 160 : count <= 6 ? 260 : 300;
  const step = spread / (count - 1);
  return Array.from({ length: count }, (_, i) => awayAngle - spread / 2 + i * step);
}

export function groupPersonSlots(count: number, awayAngle: number): number[] {
  const spread = count === 1 ? 0 : count === 2 ? 140 : count <= 4 ? 200 : count <= 6 ? 260 : 310;
  const step = spread / (count - 1 || 1);
  return Array.from({ length: count }, (_, i) => awayAngle - spread / 2 + i * step);
}

export function defaultGroupPos(gi: number, groupCount: number, cx: number, cy: number): { x: number; y: number } {
  const ang = G_ANG[gi] != null ? G_ANG[gi] : -90 + gi * (360 / Math.max(groupCount, 1));
  const r = G_R_OVERRIDE[gi] || G_R;
  return pt(r, ang, cx, cy);
}

export interface PersonEntry { ki: number; p: Person; ang: number; x: number; y: number }
export interface ProgEntry {
  gi: number; pi: number; prog: Program; pang: number; pR: number;
  pcx: number; pcy: number; discR: number; ring: number; solo: boolean; people: PersonEntry[];
}
export interface GroupEntry {
  gi: number; gr: Group; away: number; gcx: number; gcy: number;
  nProgs: number; discR: number; solo: boolean; progs: ProgEntry[];
}

export function computeLayout(groups: Group[], cx: number, cy: number): GroupEntry[] {
  return groups.map((gr, gi) => {
    const dp = defaultGroupPos(gi, groups.length, cx, cy);
    const gcx = typeof gr.x === 'number' ? gr.x : dp.x;
    const gcy = typeof gr.y === 'number' ? gr.y : dp.y;
    const away = (Math.atan2(gcy - cy, gcx - cx) * 180) / Math.PI;
    const progs = gr.programs;
    const nProgs = progs.length;
    const discR = gr.discR || G_DISC;

    let defAngles: number[];
    if (nProgs <= 1) {
      defAngles = [away];
    } else {
      const spread = nProgs <= 3 ? 160 : nProgs <= 5 ? 200 : 290;
      const step = spread / (nProgs - 1);
      defAngles = progs.map((_, i) => away - spread / 2 + i * step);
    }

    const entry: GroupEntry = {
      gi, gr, away, gcx, gcy, nProgs, discR, solo: nProgs === 1, progs: [],
    };

    progs.forEach((prog, pi) => {
      const pang = typeof prog.pang === 'number' ? prog.pang : defAngles[pi];
      const defR = nProgs === 1 ? 0 : nProgs >= 5 ? P_R + 160 : P_R + 95;
      const pR = typeof prog.pR === 'number' ? prog.pR : defR;
      const c = nProgs === 1 ? { x: gcx, y: gcy } : pt(pR, pang, gcx, gcy);
      const pDisc = nProgs === 1 ? discR : prog.discR || P_DISC;
      const defRing = nProgs === 1 ? ORBIT1_R : nProgs >= 5 ? ORBIT2_R + 40 : ORBIT2_R + 35;
      const ring = nProgs === 1 ? gr.ringR || prog.ringR || defRing : prog.ringR || defRing;

      const nP = prog.people.length;
      const slots = nProgs === 1
        ? groupPersonSlots(nP, pang + (prog.slotRot || 0))
        : personSlots(nP, pang + 180 + (pi - (nProgs - 1) / 2) * 20 + 45 + (prog.slotRot || 0));

      const people: PersonEntry[] = prog.people.map((p, ki) => {
        const a = typeof p.ang === 'number' ? p.ang : slots[ki] != null ? slots[ki] : pang;
        const q = pt(ring, a, c.x, c.y);
        return { ki, p, ang: a, x: q.x, y: q.y };
      });

      entry.progs.push({ gi, pi, prog, pang, pR, pcx: c.x, pcy: c.y, discR: pDisc, ring, solo: nProgs === 1, people });
    });

    return entry;
  });
}

export function nearestOrbit(p: { x: number; y: number }, layout: GroupEntry[]): {
  gi: number; pi: number; pe: ProgEntry; ang: number; x: number; y: number;
} | null {
  let best: { d: number; pe: ProgEntry } | null = null;
  layout.forEach(ge => ge.progs.forEach(pe => {
    const d = Math.abs(Math.hypot(p.x - pe.pcx, p.y - pe.pcy) - pe.ring);
    if (!best || d < best.d) best = { d, pe };
  }));
  if (!best) return null;
  const pe = best.pe;
  const ang = (Math.atan2(p.y - pe.pcy, p.x - pe.pcx) * 180) / Math.PI;
  const q = pt(pe.ring, ang, pe.pcx, pe.pcy);
  return { gi: pe.gi, pi: pe.pi, pe, ang, x: q.x, y: q.y };
}
